import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SecurityContext,
    SimpleChanges,
    TemplateRef,
} from '@angular/core';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Range } from 'quill/core/selection';
import { ContentChange, QuillModule } from 'ngx-quill';
import Quill from 'quill';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { finalize, take } from 'rxjs/operators';
import $ from 'jquery';

import { FormService } from '../../../common/services/form.service';
import { MessageStorageService } from '../../../common/services/message-storage.service';
import { FormCommonDataService } from '../../../common/services/form-common-data.service';
import { ClientField } from '../../../common/models/client-field.model';

import { QuillHelperService } from '@sb-tools/typescript/services/quill/quill-helper.service';
import { ReplyTemplateManagerService } from '@sb-tools/typescript/services/reply-template-manager.service';
import { SmilesService } from '@sb-tools/typescript/services/smiles.service';
import { HelperService } from '@sb-tools/typescript/services/helper.service';
import { CommonDataService } from '@sb-tools/typescript/services/common-data.service';
import { UserDataService } from '@sb-tools/typescript/services/user-data.service';
import { SignatureService } from '@sb-tools/typescript/services/signature.service';
import { Field } from '@sb-tools/typescript/models/field.model';
import { AllReplyTemplatesModel } from '@sb-tools/typescript/models/all-reply-templates-model.model';
import { InboxAttachmentBase } from '@sb-tools/typescript/models/inbox-attachment-base.model';
import { FormType } from '@sb-tools/typescript/enums/form-type.enum';
import { TicketSource } from '@sb-tools/typescript/enums/ticket-source.enum';
import { NewLinePipe } from '@sb-tools/typescript/pipes/new-line.pipe';
import { REPLY_TEMPLATE_MODAL_ID } from '@sb-tools/typescript/constants/nested-modal-ids';
import { LEFT_ARROW, RIGHT_ARROW, TAB } from '@sb-tools/typescript/constants/keycodes';
import { DomSanitizer } from '@angular/platform-browser';

import { ReplyTemplatesModalComponent } from 'src/sb-modules/Frontend-Reply-Templates-Module/reply-templates-modal.component';
import { ReplyTemplateDataService } from 'src/sb-modules/Frontend-Reply-Templates-Module/core/reply-template-data.service';
import { ReplyTemplateService } from 'src/sb-modules/Frontend-Reply-Templates-Module/core/reply-template.service';

import { environment } from '@environment';
import { SummernoteOptions } from '@form-field-module/common/types/summernote-options';
import { QuillReplyTemplatesComponent } from '../../../../Frontend-Reply-Templates-Module/quill-reply-templates/quill-reply-templates.component';
import { NgxSummernoteModule } from 'ngx-summernote';
import { LoaderComponent } from '@sb-tools/typescript/components/loader/loader.component';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { InlineSVGComponent } from '../../../../Frontend-Inline-SVG-Module/inline-svg.component';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { QuillEmojiPickerComponent } from '@sb-tools/typescript/components/quill-emoji-picker/quill-emoji-picker.component';
import { FormFieldLabelComponent } from '@form-field-module/common/components/form-field-label/form-field-label.component';

@Component({
    selector: 'sb-message',
    templateUrl: './message.component.html',
    styleUrls: ['./message.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    imports: [
        QuillReplyTemplatesComponent,
        NgxSummernoteModule,
        FormsModule,
        LoaderComponent,
        TranslateModule,
        TooltipModule,
        InlineSVGComponent,
        QuillModule,
        NgClass,
        QuillEmojiPickerComponent,
        FormFieldLabelComponent,
        NgTemplateOutlet,
    ],
})
export class MessageComponent implements OnInit, OnDestroy, OnChanges {
    @Input() activeSentimentValue: string;
    @Input() attachments?: InboxAttachmentBase[];
    @Input() attachmentsBlockTemplate?: TemplateRef<any>;
    @Input() formField: Field | ClientField;
    @Input() formType: FormType;

    editorModules;
    isAutoSignature = false;
    isDraftSupported: boolean;
    isForwardForm: boolean;
    isHtmlEditor: boolean;
    isInternalForm: boolean;
    isLocalStorageSupported = HelperService.isLocalStorageNameSupported();
    isQuillEmpty = true;
    isSignatureLoaded: boolean;
    isSummernoteEditor: boolean;
    isWithReplyTemplates: boolean;
    isWithSignature: boolean;
    quillEditor: Quill;
    quillPlaceholder = '';
    quillRange: Range;
    summernote: JQuery<HTMLElement>;
    summernoteConfig: SummernoteOptions;

    private focusedToolbarElementIndex = 0;
    private signatureLoadedSubject = new Subject<void>();
    private unsubscriber = new Subject<void>();

    constructor(
        public commonDataService: CommonDataService,
        public formService: FormService,
        public formCommonDataService: FormCommonDataService,
        public replyTemplateManagerService: ReplyTemplateManagerService,
        public replyTemplateService: ReplyTemplateService,
        private bsModalService: BsModalService,
        private domSanitizer: DomSanitizer,
        private messageStorageService: MessageStorageService,
        private newLinePipe: NewLinePipe,
        private quillHelperService: QuillHelperService,
        private replyTemplateDataService: ReplyTemplateDataService,
        private signatureService: SignatureService,
        private smilesService: SmilesService,
        private userDataService: UserDataService,
    ) {}

    ngOnInit() {
        this.formField.value = this.domSanitizer.sanitize(SecurityContext.HTML, this.formField.value);
        this.isForwardForm = this.formType === FormType.inboxForwardForm;
        this.isInternalForm = this.formType === FormType.webInternal;
        this.isDraftSupported = this.isInternalForm || this.isForwardForm;
        this.isSummernoteEditor = this.formType === FormType.outlook || this.formType === FormType.inboxForwardForm;

        this.isWithSignature = this.isForwardForm || this.isInternalForm;
        this.isWithReplyTemplates =
            (this.isInternalForm || this.isForwardForm) && this.formField.options.isWithReplyTemplates === 'true';

        if (this.isInternalForm) {
            if (
                this.formField.options?.isWithReplyTemplates === 'true' &&
                !this.replyTemplateService.isReplyTemplatesLoaded
            ) {
                this.loadReplyTemplates();
            }
            this.initSignature();
        }

        if (this.isForwardForm) {
            this.isSignatureLoaded = true;
        }

        if (this.isSummernoteEditor) {
            this.initSummernoteConfig();
            setTimeout(() => {
                this.summernote = jQuery('#form-element-' + this.formField.id);
            }, 0);
            return;
        }

        this.formField.valueModel = {
            htmlValue: this.formField.value,
            textValue: '',
        };

        this.isHtmlEditor = this.formField.options.isHtmlEditor === 'true';
        this.isAutoSignature = this.isInternalForm && this.formField.options.isAutoSignatureEnabled === 'true';

        this.editorModules = {
            ...(this.isHtmlEditor ? this.quillHelperService.htmlQuillConfig : this.quillHelperService.textQuillConfig),
            keyboard: {
                bindings: {
                    tab: {
                        key: TAB,
                        handler() {
                            $('#btn-emoji').focus();
                        },
                    },
                },
            },
        };

        if (this.formField.options?.isMessageDependOnSentiment === 'true') {
            this.setPlaceholder('question');
        } else {
            this.quillPlaceholder = this.formField.placeholder;
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.activeSentimentValue && changes.activeSentimentValue.currentValue) {
            this.setPlaceholder(changes.activeSentimentValue.currentValue);
        }
    }

    onContentChanged(data: ContentChange) {
        this.formField.valueModel.textValue = HelperService.strip(
            this.smilesService.replaceImagesToCodes(data.html),
            false,
        );
        this.formField.value =
            this.isHtmlEditor || this.isInternalForm
                ? data.html
                : HelperService.strip(this.smilesService.replaceImagesToCodes(data.html), false);

        this.isQuillEmpty = this.quillHelperService.checkQuillEmpty(this.quillEditor);

        if (this.isDraftSupported && data.source === 'user') {
            this.messageStorageService.updateDraftMessage(this.formField.value, this.isForwardForm);
        }
    }

    onEditorCreated(quill) {
        this.quillEditor = quill;

        if (this.isAutoSignature && !this.messageStorageService.getDraftMessage(this.isForwardForm)) {
            if (!this.isSignatureLoaded) {
                this.signatureLoadedSubject.pipe(takeUntil(this.unsubscriber)).subscribe(() => {
                    this.setSignature({ target: $('#btn-signature')[0] }, !this.formField.isNeedToFocus);
                    this.quillEditor.setSelection(0);
                });
            } else {
                this.setSignature({ target: $('#btn-signature')[0] }, !this.formField.isNeedToFocus);
                this.quillEditor.setSelection(0);
            }
        }

        if (this.isHtmlEditor) {
            quill.getModule('toolbar').addHandler('image', () => {
                this.quillHelperService.selectLocalImage(quill);
            });
        }

        if (this.isDraftSupported) {
            const draft = this.messageStorageService.getDraftMessage(this.isForwardForm);

            if (draft) {
                this.formField.value = this.domSanitizer.sanitize(SecurityContext.HTML, draft);
                this.formField.valueModel.htmlValue = this.formField.value;
            }
        }

        if (this.isHtmlEditor) {
            this.addToolbarAccessibilityFromKeyboard();
        }

        if (this.formField.isNeedToFocus) {
            setTimeout(() => {
                this.quillEditor.setSelection(0);
                this.quillEditor.focus();
            }, 100);
        }

        this.isQuillEmpty = this.quillHelperService.checkQuillEmpty(this.quillEditor);
    }

    onSummernoteContentChanged(): void {
        if (this.isDraftSupported) {
            this.messageStorageService.updateDraftMessage(this.formField.value, this.isForwardForm);
        }
    }

    openReplyTemplatesModal($event, quillEditor): void {
        this.formCommonDataService.isReplyTemplatesModalOpened = true;
        this.updateQuillSelection(quillEditor);

        const initialState = {
            modalId: REPLY_TEMPLATE_MODAL_ID,
            isTextContent: !this.isHtmlEditor,
            confirmCallback: (templateText, attachments) => {
                this.formCommonDataService.attachmentsAddedSubject.next(attachments);
                return this.replyTemplateManagerService.chooseReplyTemplate({
                    $event,
                    templateText: this.isHtmlEditor ? templateText : this.newLinePipe.transform(templateText),
                    quillEditor,
                    quillRange: this.quillRange,
                });
            },
        };

        this.bsModalService.show(ReplyTemplatesModalComponent, {
            initialState,
            id: REPLY_TEMPLATE_MODAL_ID,
        });

        this.bsModalService.onHide.pipe(takeUntil(this.unsubscriber)).subscribe(() => {
            this.formCommonDataService.isReplyTemplatesModalOpened = false;
        });
    }

    openReplyTemplatesModalToSummernote(): void {
        this.summernote['summernote']('editor.saveRange');
        this.formCommonDataService.isReplyTemplatesModalOpened = true;

        const initialState = {
            modalId: REPLY_TEMPLATE_MODAL_ID,
            isTextContent: false,
            confirmCallback: (templateText: string, attachments): void => {
                this.summernote['summernote']('editor.restoreRange');
                this.summernote['summernote']('pasteHTML', templateText);

                if (this.attachments && attachments?.length) {
                    this.attachments.push(...attachments);
                }
            },
        };

        this.bsModalService.show(ReplyTemplatesModalComponent, {
            initialState,
            id: REPLY_TEMPLATE_MODAL_ID,
        });

        this.bsModalService.onHide.pipe(takeUntil(this.unsubscriber)).subscribe(() => {
            this.formCommonDataService.isReplyTemplatesModalOpened = false;
        });
    }

    resetFocusedToolbarElementIndex() {
        this.focusedToolbarElementIndex = 0;
    }

    saveSummernoteRange(): void {
        this.summernote['summernote']('editor.saveRange');
    }

    setEmojiToSummernote(emoji: any): void {
        const emojiNode = emoji.native;
        this.summernote['summernote']('editor.restoreRange');
        this.summernote['summernote']('pasteHTML', emojiNode);
        // const emojiNode = this.replaceEmojisPipe.transform(emoji.native)['changingThisBreaksApplicationSecurity'];
        // const node = document.createElement('div');
        // node.innerHTML = emojiNode;
        // summernote['summernote']('insertNode', node);
    }

    setSignature(event, withoutFocus): void {
        const signature = this.signatureService.getSignature(TicketSource.form);
        this.replyTemplateManagerService.chooseReplyTemplate({
            $event: event,
            templateText: signature,
            quillEditor: this.quillEditor,
            quillRange: null,
            isSmile: false,
            isSetCaretAtStart: true,
            isFocusNotNeeded: withoutFocus,
        });
    }

    setSignatureToSummernote(): void {
        const signature = this.commonDataService.signatures.socialBoards.text || '';
        const signatureWithoutLineBreaks = signature.replace(new RegExp('(&#10;)|(\\n)|(\\r\\n)', 'g'), '');
        const node = document.createElement('div');
        node.innerHTML = signatureWithoutLineBreaks;
        this.summernote['summernote']('insertNode', node);
    }

    private addToolbarAccessibilityFromKeyboard(): void {
        setTimeout(() => {
            this.processToolbarPickers();
        });
        const focusableToolbarElements = document.querySelectorAll('button.ql-focusable, span.ql-picker-label') as any;
        const focusableToolbarElementsCount = focusableToolbarElements.length;

        $('#toolbar-quill').on('keydown', (event) => {
            if (event.keyCode === RIGHT_ARROW) {
                event.preventDefault();

                this.focusedToolbarElementIndex =
                    this.focusedToolbarElementIndex + 1 >= focusableToolbarElementsCount
                        ? 0
                        : this.focusedToolbarElementIndex + 1;

                focusableToolbarElements[this.focusedToolbarElementIndex].focus();

                return;
            }
            if (event.keyCode === LEFT_ARROW) {
                event.preventDefault();
                this.focusedToolbarElementIndex =
                    this.focusedToolbarElementIndex - 1 >= 0
                        ? this.focusedToolbarElementIndex - 1
                        : focusableToolbarElementsCount - 1;
                focusableToolbarElements[this.focusedToolbarElementIndex].focus();
            }
        });
    }

    private disablePickerLabelsForTab() {
        const pickerLabelElements = document.querySelectorAll('.ql-picker-label') as any;

        for (const element of pickerLabelElements) {
            element.setAttribute('tabindex', -1);
            element.setAttribute('aria-hidden', true);
        }
    }

    private initSignature(): void {
        this.userDataService
            .getSignatures()
            .pipe(takeUntil(this.unsubscriber), take(1))
            .subscribe((signatures) => {
                if (signatures.socialBoards.text) {
                    signatures.socialBoards.text = this.quillHelperService.convertImageSizeFromStylesToAttributes(
                        signatures.socialBoards.text,
                    );
                }
                this.commonDataService.signatures = signatures;
                this.isSignatureLoaded = true;
                this.signatureLoadedSubject.next();
            });
    }

    private initSummernoteConfig() {
        this.summernoteConfig = {
            height: 300,
            toolbar: [
                ['edit', ['undo', 'redo']],
                ['headline', ['style']],
                ['alignment', ['ul', 'ol', 'paragraph', 'lineheight']],
                ['style', ['bold', 'italic', 'underline', 'strikethrough', 'clear']],
                ['textsize', ['fontsize']],
                ['fontclr', ['color']],
            ],
            disableDragAndDrop: true,
            placeholder: this.formField.placeholder,
            popover: {
                image: [
                    ['custom', ['imageAttributes']],
                    ['imagesize', ['imageSize100', 'imageSize50', 'imageSize25']],
                    ['float', ['floatLeft', 'floatRight', 'floatNone']],
                    ['remove', ['removeMedia']],
                ],
            },
            uploadImagePath: environment.uploadsUrl + 'attachments',
        } as any; // 'any' set here because property 'disableDragAndDrop' doesn't exist in SummernoteOptions,
        // although it's supported in editor

        if (this.isDraftSupported) {
            const draft = this.messageStorageService.getDraftMessage(this.isForwardForm);

            if (draft) {
                this.formField.value = this.domSanitizer.sanitize(SecurityContext.HTML, draft);
            }
        }
    }

    private loadReplyTemplates(): void {
        this.replyTemplateDataService
            .getAllReplyTemplates()
            .pipe(
                finalize(() => {
                    this.replyTemplateService.isReplyTemplatesLoaded = true;
                }),
                takeUntil(this.unsubscriber),
                take(1),
            )
            .subscribe(({ community, personal }: AllReplyTemplatesModel) => {
                this.replyTemplateService.replyTemplatesTree = community;
                this.replyTemplateService.personalReplyTemplatesTree = personal;
                this.replyTemplateService.personalReplyTemplates = [...personal.byCategories, ...personal.global];
                this.replyTemplateService.replyTemplates = [...community.byCategories, ...community.global];
                this.replyTemplateService.allReplyTemplates = this.replyTemplateService.personalReplyTemplates.concat(
                    this.replyTemplateService.replyTemplates,
                );
            });
    }

    private processToolbarPickers() {
        this.disablePickerLabelsForTab();
        this.setAriaLabelToClickableSpan();
    }

    private setAriaLabelToClickableSpan() {
        const pickerElements = document.querySelectorAll('select.ql-focusable') as any;

        for (const picker of pickerElements) {
            picker.setAttribute('aria-hidden', true);

            const clickableSpan = picker.previousSibling;
            const ariaLabelValue = picker.getAttribute('aria-label');
            clickableSpan?.setAttribute('aria-label', ariaLabelValue);
            clickableSpan?.setAttribute('role', 'button');
        }
    }

    private setPlaceholder(activeSentimentValue) {
        if (this.formField.options.isMessageDependOnSentiment === 'true' && activeSentimentValue) {
            this.quillPlaceholder =
                (this.formField.options[activeSentimentValue + 'Placeholder'] || '') +
                (this.formField.isRequired ? ' *' : '');
        } else {
            this.quillPlaceholder = this.formField.placeholder;
        }

        if (this.quillEditor) {
            this.quillEditor.root.setAttribute('data-placeholder', this.quillPlaceholder);
        }
    }

    public updateQuillSelection(quill) {
        this.quillRange = this.replyTemplateManagerService.updateRange(quill);
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
